<template>
  <div id="request-listing">
    <b-row class="request-row">
      <b-col md="12">
        <h1>Request To Add A Partnership</h1>
        <p>
          This survey is part of a project funded by the U.S. Department of State
          to capture current tie-ups between American and Indian colleges and universities.
          Results will be included in this online database accessible to the public.
          Respondents will be notified about Fulbright program opportunities and new
          fellowships and grant opportunities.
        </p>
      </b-col>
    </b-row>
    <br>
    <b-row class="request-row">
      <b-col md="12">
        <b-alert variant="success" v-model="success">
          Your Partnership request has been submitted. It will be reviewed shortly.
        </b-alert>
        <b-alert variant="danger" v-model="failure">
          <ul>
            <li v-for="error in errors">
              {{ error[0] }}
            </li>
          </ul>
        </b-alert>
        <b-form @submit.prevent="submitRequest" v-if="!success">
          <b-row>
            <b-col md="12">
              <h5>Partnership Information</h5>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="12">
              <b-form-group
                label="Name of Partnership/Project *"
                label-for="name">
                <p class="label-help">
                  This is the name displayed on the Partnership's listing page.
                  If the Partnership doesn't have a specific name,
                  the names of the participating institutions separated by a
                  hyphen may be used. (i.e. University 1 - University 2)
                </p>
                <b-input
                  id="name"
                  type="text"
                  v-model="request.name"
                  autocorrect="off"
                  required>
                </b-input>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="12">
              <b-form-group
                label="Description"
                label-for="description">
                  <p class="label-help">
                    This is a brief description of the Partnership to be shown
                    on the Partnership's listing page. (optional)
                  </p>
                  <b-form-textarea
                    id="description"
                    rows="5"
                    max-rows="5"
                    no-resize
                    v-model="request.description">
                  </b-form-textarea>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="6">
              <b-form-group
                label="Year Established *"
                label-for="year-established">
                  <p class="label-help">
                    This is the year the Partnership was first established.
                    <br> <br>
                  </p>
                  <b-form-select
                    id="year-established"
                    :options="yearEstablishedOptions"
                    v-model="request.year_established"
                    required>
                  </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Expected Tenure"
                label-for="end-date">
                  <p class="label-help">
                    Use "In perpetuity" or "N/A" if there is no
                    projected end date. (optional)
                  </p>
                  <b-form-input
                    id="end-date"
                    type="text"
                    v-model="request.end_date"
                    autocorrect="off">
                  </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="12">
              <b-form-group
                label="Website (URL)"
                label-for="website">
                  <p class="label-help">
                    If the Partnership has a dedicated website or URL that
                    should be displayed on the Partnership's listing page,
                    it may be included here. (optional)
                  </p>
                  <b-form-input
                    id="website"
                    type="text"
                    v-model="request.website"
                    autocapitalize="off"
                    autocorrect="off">
                  </b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="6">
              <b-form-group
                label="Type *"
                label-for="type">
                  <p class="label-help">
                    Choose the type that best identifies your program.
                  </p>
                  <b-form-select
                    id="type"
                    :options="type_options"
                    text-field="name"
                    value-field="name"
                    v-model="request.type"
                    required>
                  </b-form-select>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group
                label="Sector *"
                label-for="sector">
                  <p class="label-help">
                    Choose the sector that best identifies your program.
                  </p>
                  <b-form-select
                    id="sector"
                    :options="sector_options"
                    text-field="name"
                    value-field="name"
                    v-model="request.sector"
                    required>
                  </b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="12">
              <b-form-group
                label="Funding Source(s)"
                label-for="grant-funding">
                  <p class="label-help">
                    List or description of Partnership funding source(s)
                    to be shown on the Partnership's listing page. (optional)
                  </p>
                  <b-form-textarea
                    id="grant-funding"
                    rows="5"
                    max-rows="5"
                    no-resize
                    v-model="request.grant_funding">
                  </b-form-textarea>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="12">
              <h5>Partnership Contact</h5>
              <p class="label-help">
                This is the main contact for the Partnership. This person will be contacted
                if additional Partnership details are needed. This information will
                be displayed on the Partnership's listing page.
              </p>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <b-row>
                <b-col md="4">
                  <b-form-group
                    label="Contact Name"
                    label-for="contact-name">
                      <b-form-input
                        id="contact-name"
                        type="text"
                        v-model="request.contact_name"
                        autocorrect="off">
                      </b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Contact Email *"
                    label-for="contact-email">
                      <b-form-input
                        id="contact-email"
                        type="email"
                        v-model="request.contact_email"
                        autocapitalize="off"
                        autocorrect="off"
                        required>
                      </b-form-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Contact Phone"
                    label-for="contact-phone">
                      <b-form-input
                        id="contact-phone"
                        type="tel"
                        v-model="request.contact_phone">
                      </b-form-input>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <br>
          <div id="institutions">
            <b-row>
              <b-col md="12">
                <h5>Partner Institutions</h5>
                <p class="label-help">
                  The universities and other institutions that are part of the Partnership.
                </p>
              </b-col>
            </b-row>
            <div class="institution-group">
              <b-row>
                <b-col md="12">
                  <h6>
                    <strong>U.S. Institution</strong>
                  </h6>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <b-form-group
                    label="Institution *"
                    label-for="us-institution">
                    <b-input
                      id="us-institution"
                      type="text"
                      v-model="request.us_institution.name"
                      autocorrect="off"
                      required>
                    </b-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Department/Unit"
                    label-for="us-institution-unit">
                    <b-input
                      id="us-institution-unit"
                      type="text"
                      v-model="request.us_institution.unit"
                      autocorrect="off">
                    </b-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="State/Province *"
                    label-for="us-institution-state-province">
                    <b-form-select
                      id="us-institution-state-province"
                      :options="usStateProvinceOptions"
                      text-field="name"
                      value-field="name"
                      v-model="request.us_institution.state_province"
                      required>
                    </b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
            <div
              class="institution-group"
              v-for="(additional_institution, index) in request.additional_institutions"
              :key="index">
                <b-row v-if="additional_institution.type === 'US'">
                  <b-col md="11">
                    <b-row>
                      <b-col md="4">
                        <b-form-group
                          label="Institution *"
                          :label-for="'additional-institution-' + (index + 1)">
                            <b-input
                              :id="'additional-institution-' + (index + 1)"
                              type="text"
                              v-model="request.additional_institutions[index].name"
                              autocorrect="off"
                              required>
                            </b-input>
                        </b-form-group>
                      </b-col>
                      <b-col md="4">
                        <b-form-group
                          label="Department/Unit"
                          :label-for="'additional-institution-unit' + (index + 1)">
                            <b-input
                              :id="'additional-institution-unit' + (index + 1)"
                              type="text"
                              v-model="request.additional_institutions[index].unit"
                              autocorrect="off">
                            </b-input>
                        </b-form-group>
                      </b-col>
                      <b-col md="4">
                        <b-form-group
                          label="State/Province *"
                          :label-for="'additional-institution-state-province' + (index + 1)">
                            <b-form-select
                              :id="'additional-institution-state-province' + (index + 1)"
                              :options="usStateProvinceOptions"
                              text-field="name"
                              value-field="name"
                              v-model="request.additional_institutions[index].state_province"
                              required>
                            </b-form-select>
                        </b-form-group>
                      </b-col>
                    </b-row>
                  </b-col>
                  <b-col md="1">
                    <b-form-group
                      label="&nbsp;">
                        <b-btn
                          block
                          variant="secondary"
                          type="button"
                          @click="removeAdditionalInstitution(index)">
                            -
                        </b-btn>
                    </b-form-group>
                  </b-col>
                </b-row>
            </div>
            <b-row>
              <b-col md="12">
                <p>
                  Click <b-link variant="link" @click="addAdditionalInstitution('US')">here</b-link>
                  to add another U.S. institution
                </p>
              </b-col>
            </b-row>
            <div class="institution-group">
              <b-row>
                <b-col md="12">
                  <h6>
                    <strong>Indian Institution</strong>
                  </h6>
                </b-col>
              </b-row>
              <b-row>
                <b-col md="4">
                  <b-form-group
                    label="Institution *"
                    label-for="indian-institution">
                    <b-input
                      id="indian-institution"
                      type="text"
                      v-model="request.indian_institution.name"
                      autocorrect="off"
                      required>
                    </b-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="Department/Unit"
                    label-for="indian-institution-unit">
                    <b-input
                      id="indian-institution-unit"
                      type="text"
                      v-model="request.indian_institution.unit"
                      autocorrect="off">
                    </b-input>
                  </b-form-group>
                </b-col>
                <b-col md="4">
                  <b-form-group
                    label="State/Province *"
                    label-for="indian-institution-state-province">
                    <b-form-select
                      id="indian-institution-state-province"
                      :options="indiaStateProvinceOptions"
                      text-field="name"
                      value-field="name"
                      v-model="request.indian_institution.state_province"
                      required>
                    </b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </div>
          <div
            class="institution-group"
            v-for="(additional_institution, index) in request.additional_institutions"
            :key="index">
              <b-row v-if="additional_institution.type === 'IN'">
                <b-col md="11">
                  <b-row>
                    <b-col md="4">
                      <b-form-group
                        label="Institution *"
                        :label-for="'additional-institution-' + (index + 1)">
                          <b-input
                            :id="'additional-institution-' + (index + 1)"
                            type="text"
                            v-model="request.additional_institutions[index].name"
                            autocorrect="off"
                            required>
                          </b-input>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group
                        label="Department/Unit"
                        :label-for="'additional-institution-unit' + (index + 1)">
                          <b-input
                            :id="'additional-institution-unit' + (index + 1)"
                            type="text"
                            v-model="request.additional_institutions[index].unit"
                            autocorrect="off">
                          </b-input>
                      </b-form-group>
                    </b-col>
                    <b-col md="4">
                      <b-form-group
                        label="State/Province *"
                        :label-for="'additional-institution-state-province' + (index + 1)">
                        <b-form-select
                          :id="'additional-institution-state-province' + (index + 1)"
                          :options="indiaStateProvinceOptions"
                          text-field="name"
                          value-field="name"
                          v-model="request.additional_institutions[index].state_province"
                          required>
                        </b-form-select>
                      </b-form-group>
                    </b-col>
                  </b-row>
                </b-col>
                <b-col md="1">
                  <b-form-group
                    label="&nbsp;">
                      <b-btn
                        block
                        variant="secondary"
                        type="button"
                        @click="removeAdditionalInstitution(index)">
                          -
                      </b-btn>
                  </b-form-group>
                </b-col>
              </b-row>
          </div>
          <b-row>
            <b-col md="12">
              <p>
                Click <b-link variant="link" @click="addAdditionalInstitution('IN')">here</b-link>
                to add another India institution
              </p>
            </b-col>
          </b-row>
          <b-row>
            <b-col md="12">
              <b-form-group>
                <b-form-checkbox
                  id="terms-agreement"
                  v-model="request.terms_agreement"
                  required>
                </b-form-checkbox>
                <label for="terms-agreement">
                  I agree to allow the above information to be published.
                </label>
              </b-form-group>
            </b-col>
          </b-row>
          <br>
          <b-row>
            <b-col md="12">
              <b-form-group>
                <b-btn
                  block
                  variant="primary"
                  type="submit">
                    Submit
                </b-btn>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  name: 'RequestListing',
  computed: {
    usStateProvinceOptions () {
      return this.$root.stateProvinceOptions.US
    },
    indiaStateProvinceOptions () {
      return this.$root.stateProvinceOptions.IN
    },
    yearEstablishedOptions () {
      let years = []
      const currentYear = (new Date()).getFullYear()
      const lastCentury = (((currentYear / 100) - ((currentYear % 100) / 100)) - 1) * 100
      const nextCentury = (((currentYear / 100) - ((currentYear % 100) / 100)) + 1) * 100

      for (let i = lastCentury; i < nextCentury; i++) {
        years.push(i)
      }

      return years
    }
  },
  data: () => ({
    success: false,
    failure: false,
    errors: {},
    request: {
      name: '',
      description: '',
      year_established: '',
      end_date: '',
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      website: '',
      type: '',
      sector: '',
      grant_funding: '',
      us_institution: {
        name: '',
        state_province: '',
        unit: ''
      },
      indian_institution: {
        name: '',
        state_province: '',
        unit: ''
      },
      additional_institutions: [],
      terms_agreement: false,
      g_recaptcha_response: ''
    },
    sector_options: [],
    type_options: []
  }),
  methods: {
    addAdditionalInstitution (type) {
      this.request.additional_institutions.push({
        name: '',
        state_province: '',
        unit: '',
        type: type
      })
    },
    removeAdditionalInstitution (index) {
      this.request.additional_institutions.splice(index, 1)
    },
    async submitRequest () {
      this.request.g_recaptcha_response = (process.env.VUE_APP_APP_ENV === 'production')
        ? await this.$recaptcha('request')
        : ''

      this.axios({
        method: 'post',
        url: '/program-request',
        data: this.request
      }).then(response => {
        this.errors = {}
        this.failure = false
        this.success = true
      }).catch(error => {
        this.errors = error.response.data.errors
        this.failure = true
        this.success = false
      })
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.$root.setTitle('Request Listing')

      vm.axios({
        method: 'get',
        url: 'sectors'
      }).then(response => {
        vm.sector_options = response.data.sort()
      })

      vm.axios({
        method: 'get',
        url: 'types'
      }).then(response => {
        vm.type_options = response.data.sort()
      })
    })
  }
}
</script>

<style lang="scss" scoped>
  .institution-group {
    margin-top: 2%;
    margin-bottom: 2%;
  }

  #request-listing {
    padding-top: 2rem;
    margin-left: auto;
    margin-right: auto;
    width: 45rem;
  }

  h1 {
    padding-bottom: 1rem;
  }

  p {
    padding-bottom: 0;
  }

  a {
    font-size: 1.05rem !important;
  }

  p.label-help {
    font-size: 0.9rem;
  }

  @media screen and (max-width: 767px) {
    .institution-group {
      background-color: rgb(188, 187, 186);
      border-radius: 0.25rem;
      padding: 4% 4% 4% 4%;
      margin-bottom: 4%;
    }

    #request-listing {
      width: 80%;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/RequestListing.vue